import type { ParseKeys } from 'i18next'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Field, Select, Textarea} from '@/components/ui/Field'
import { toOptions } from '@/components/ui/control'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import type {
  DocumentTemplate,
  IssuingEntity,
  NumberingScheme,
  Office,
} from '@/types/domain'

/* The settings editors: issuing entities, numbering schemes, document templates.
 *
 * These three are super-admin surfaces and they change rarely, but they are the
 * ones that decide what every document in the product LOOKS like — branding,
 * bank details and the legal footer resolve from the issuing entity rather than
 * being typed (FR-64), and that is exactly what lets the two offices issue
 * documents that differ correctly.
 */

/* ── Issuing entity ───────────────────────────────────────────────────────── */

export interface EntityDraft {
  name: string
  /** Expands {INITIALS} in a numbering pattern. */
  initials: string
  /** Expands {OFFICE}. */
  officeCode: string
  /** The office whose records default to this entity (FR-24); '' for none. */
  defaultOffice: Office | ''
  legalFooter: string
  bankDetails: string
  emailSignature: string
}

export function EntityFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
}: {
  open: boolean
  onClose: () => void
  record?: IssuingEntity
  onSubmit: (values: EntityDraft) => void
  pending?: boolean
}) {
  const { t } = useTranslation()
  const [draft, setDraft, touched, markTouched] = useFormDraft<EntityDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            name: record.name,
            initials: record.initials,
            officeCode: record.officeCode,
            defaultOffice: record.defaultOffice,
            legalFooter: record.legalFooter,
            bankDetails: record.bankDetails,
            emailSignature: record.emailSignature,
          }
        : emptyEntity,
  )

  const nameError = touched && !draft.name.trim() ? t('common.form.required') : undefined
  const initialsError =
    touched && !draft.initials.trim() ? t('common.form.required') : undefined
  const officeCodeError =
    touched && !draft.officeCode.trim() ? t('common.form.required') : undefined
  const invalid =
    !draft.name.trim() || !draft.initials.trim() || !draft.officeCode.trim()

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('settings.entities.edit') : t('settings.entities.new')}
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field label={t('settings.entities.name')} required error={nameError}>
        {(props) => (
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* The two short codes a numbering pattern expands. They are read aloud
          on the phone and typed into a client's own system, so they are their
          own fields rather than derived from the name: "ASA Logistics Yiwu" is
          not "YW", and no rule turns one into the other (FR-68). */}
      <div className="grid grid-cols-2 gap-3">
        <Field
          label={t('settings.entities.initials')}
          hint={t('settings.entities.initialsHint')}
          required
          error={initialsError}
        >
          {(props) => (
            <Input
              value={draft.initials}
              onChange={(e) => setDraft({ ...draft, initials: e.target.value })}
              className="font-mono"
              {...props}
            />
          )}
        </Field>

        <Field
          label={t('settings.entities.officeCode')}
          hint={t('settings.entities.officeCodeHint')}
          required
          error={officeCodeError}
        >
          {(props) => (
            <Input
              value={draft.officeCode}
              onChange={(e) => setDraft({ ...draft, officeCode: e.target.value })}
              className="font-mono"
              {...props}
            />
          )}
        </Field>
      </div>

      {/* Which office's records default to this entity (FR-24). At most one
          entity may claim an office — the server refuses a second with a
          conflict rather than leaving "which entity" a choice nobody made. */}
      <Field
        label={t('settings.entities.defaultOffice')}
        hint={t('settings.entities.defaultOfficeHint')}
      >
        {(props) => (
          <Select
            value={draft.defaultOffice}
            onChange={(defaultOffice) => setDraft({ ...draft, defaultOffice })}
            options={[
              { value: '' as const, label: t('settings.entities.noDefaultOffice') },
              { value: 'Ashgabat' as const, label: 'Ashgabat' },
              { value: 'Yiwu' as const, label: 'Yiwu' },
            ]}
            {...props}
          />
        )}
      </Field>

      {/* These three are long-form and appear verbatim on issued documents, so
          they are textareas rather than inputs — a single-line field would hide
          most of what the recipient will actually read. */}
      <Field
        label={t('settings.entities.legalFooter')}
        hint={t('settings.entities.legalFooterHint')}
      >
        {(props) => (
          <Textarea
            value={draft.legalFooter}
            onChange={(e) => setDraft({ ...draft, legalFooter: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('settings.entities.bankDetails')}>
        {(props) => (
          <Textarea
            rows={4}
            value={draft.bankDetails}
            onChange={(e) => setDraft({ ...draft, bankDetails: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('settings.entities.emailSignature')}>
        {(props) => (
          <Textarea
            value={draft.emailSignature}
            onChange={(e) => setDraft({ ...draft, emailSignature: e.target.value })}
            {...props}
          />
        )}
      </Field>
    </FormSheet>
  )
}

const emptyEntity: EntityDraft = {
  name: '',
  initials: '',
  officeCode: '',
  defaultOffice: '',
  legalFooter: '',
  bankDetails: '',
  emailSignature: '',
}

/* ── Numbering scheme ─────────────────────────────────────────────────────── */

export interface NumberingDraft {
  pattern: string
}

/* Every kind the server accepts, in the order the templates panel lists them and
   the order the offer prints the printed three (settings/templates.go states the
   same list in TemplateKinds()). `cost-item` is last because it is the one that
   never reaches a document: it is a standing charge-line label a costing starts
   from, kept on this table because a maintained label with an optional remark is
   exactly what this table already is. */
const TEMPLATE_KINDS: DocumentTemplate['kind'][] = [
  'included',
  'not-included',
  'disclaimer',
  'cost-item',
]

/** The heading each kind is known by, on the sheet's type select and on the
 *  panel's four cards. A record keyed by the union, so a fifth kind on the wire
 *  fails to compile here rather than rendering under a neighbour's name. */
const templateKindLabelKey = {
  included: 'settings.templates.included',
  'not-included': 'settings.templates.notIncluded',
  disclaimer: 'settings.templates.disclaimers',
  'cost-item': 'settings.templates.costItems',
} as const satisfies Record<DocumentTemplate['kind'], ParseKeys>

/** The six tokens the server's pattern language accepts (FR-68, AD-12).
 *
 * These are the SERVER's spellings, verbatim. {seq} is lower-case and takes an
 * optional width — {seq:4} pads to 0001 — which is what makes ASADN26070001 and
 * AB-2 expressible in one language. A token spelled any other way is not
 * expanded: it is emitted literally into a document number nobody can undo,
 * which is why they are inserted by button rather than typed. */
const PATTERN_TOKENS = [
  '{INITIALS}',
  '{OFFICE}',
  '{YY}',
  '{YYYY}',
  '{MM}',
  '{seq}',
] as const

/** The tokens a COMPANY-WIDE pattern may use — the client registry's.
 *
 * Only the sequence. The series never resets, so a period token would let a
 * later pattern change mint a number already issued; and it belongs to no
 * issuing entity, so {INITIALS} and {OFFICE} have nothing to expand to. The
 * server refuses the others (`period-token-not-allowed`,
 * `entity-token-not-allowed`); offering them as buttons would be offering a
 * refusal. */
const COMPANY_PATTERN_TOKENS = ['{seq}'] as const

/* There is deliberately no client-side pattern expander here.
 *
 * One existed and rendered a preview from its own reimplementation of the token
 * language — with different token names — so the example a founder configured
 * against was not what the server would mint. The preview is now asked of the
 * server, which also means it carries the REAL counter value rather than a
 * guessed sequence of 1. One language, one implementation. */

export function NumberingFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
  example,
  exampleError,
  onPatternChange,
}: {
  open: boolean
  onClose: () => void
  /** Always present: a scheme is edited, never created — every (entity, kind)
   *  pair already exists on the screen, configured or defaulted. */
  record?: NumberingScheme
  onSubmit: (values: NumberingDraft) => void
  pending?: boolean
  /** The live example, rendered by the SERVER from the pattern being typed. */
  example?: string
  /** A translated message when the server refused the pattern. */
  exampleError?: string
  onPatternChange: (pattern: string) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft, touched, markTouched] = useFormDraft<NumberingDraft>(
    open && record
      ? `${record.scope}|${record.entityId ?? 'company'}|${record.documentKind}`
      : null,
    () => ({ pattern: record?.pattern ?? '' }),
  )

  if (!record) return null

  const patternError =
    touched && !draft.pattern.trim() ? t('common.form.required') : undefined
  const invalid = !draft.pattern.trim()

  const setPattern = (pattern: string) => {
    setDraft({ pattern })
    onPatternChange(pattern)
  }

  const company = record.scope === 'company'
  const tokens = company ? COMPANY_PATTERN_TOKENS : PATTERN_TOKENS

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('settings.numbering.edit')}
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      {/* Entity and document kind identify WHICH scheme this is; they are not
          editable here. Changing either would not amend this scheme, it would
          address a different one — and every pair already exists on the screen
          behind this sheet. A company-wide scheme has no entity, and says so
          rather than showing an empty one. */}
      <div className="bg-surface-sunken border-line flex flex-col gap-1 rounded-md border px-3 py-2.5">
        <span className="asa-eyebrow">{t('settings.numbering.entity')}</span>
        <span className="text-text-heading text-[13px]">
          {company ? t('settings.numbering.company.title') : record.entityName}
        </span>
        <span className="asa-eyebrow pt-1.5">{t('settings.numbering.document')}</span>
        <span className="text-text-heading text-[13px]">
          {t(`settings.numbering.kinds.${record.documentKind}`)}
        </span>
      </div>

      <Field label={t('settings.numbering.pattern')} required error={patternError}>
        {(props) => (
          <Input
            value={draft.pattern}
            onChange={(e) => setPattern(e.target.value)}
            className="font-mono"
            {...props}
          />
        )}
      </Field>

      {/* Tokens are inserted rather than typed: they are the one part of the
          pattern where a typo is silent — a misspelled token is not expanded,
          it is emitted literally into a document number nobody can undo. */}
      <div className="-mt-1 flex flex-wrap gap-1.5">
        {tokens.map((token) => (
          <button
            key={token}
            type="button"
            onClick={() => setPattern(draft.pattern + token)}
            className="border-border-default text-text-muted hover:border-brand hover:text-brand cursor-pointer rounded-md border px-2 py-1 font-mono text-[11.5px] transition-colors"
          >
            {token}
          </button>
        ))}
      </div>

      {/* The live example is the point of editing a pattern here rather than in
          a config file: the effect of a change is visible before it is saved,
          not on the next document issued (FR-68). It comes from the server, so
          it is what would actually be minted — including the real counter
          value, not a guessed sequence of 1. */}
      <div className="bg-surface-sunken border-line flex flex-col gap-1 rounded-md border px-3 py-2.5">
        <span className="asa-eyebrow">{t('settings.numbering.liveExample')}</span>
        {exampleError ? (
          <span className="text-negative text-[12.5px] leading-snug">{exampleError}</span>
        ) : (
          <span className="text-text-heading font-mono text-[13px] font-semibold">
            {example || '—'}
          </span>
        )}
      </div>

      <p className="text-text-muted text-[12px] leading-snug font-light">
        {company ? t('settings.numbering.company.changeNote') : t('settings.numbering.changeNote')}
      </p>
    </FormSheet>
  )
}

/* ── Document template ────────────────────────────────────────────────────── */

export interface TemplateDraft {
  label: string
  body: string
  kind: DocumentTemplate['kind']
}

export function TemplateFormSheet({
  open,
  onClose,
  record,
  kind,
  onSubmit,
  pending,
  onRemove,
  removePending,
}: {
  open: boolean
  onClose: () => void
  record?: DocumentTemplate
  /** Which group the add button belongs to — one of the four the panel shows. */
  kind: DocumentTemplate['kind']
  onSubmit: (values: TemplateDraft) => void
  pending?: boolean
  /** Removes this template. Absent when adding, since there is nothing to
   *  remove — which is what makes the control's absence the rule rather than a
   *  disabled button nobody can explain (invariants: client-ui). */
  onRemove?: () => void
  removePending?: boolean
}) {
  const { t } = useTranslation()
  const [draft, setDraft, touched, markTouched] = useFormDraft<TemplateDraft>(
    open ? `${record?.id ?? 'new'}:${kind}` : null,
    () =>
      record
        ? { label: record.label, body: record.body, kind: record.kind }
        : { ...emptyTemplate, kind },
  )

  /* The body is required for the kinds that PRINT and optional for the one that
     does not, which is migration 0042's CHECK and settings.TemplateChange's rule
     rendered rather than re-decided. A printed block with no body puts an empty
     heading on a client's offer; a charge line's body is a remark, and blank is
     the ordinary state — all seven seeded presets ship with one. */
  const bodyOptional = draft.kind === 'cost-item'

  const labelError =
    touched && !draft.label.trim() ? t('common.form.required') : undefined
  const bodyError =
    touched && !bodyOptional && !draft.body.trim() ? t('common.form.required') : undefined
  const invalid = !draft.label.trim() || (!bodyOptional && !draft.body.trim())

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('settings.templates.edit') : t('settings.templates.new')}
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field label={t('settings.templates.label')} required error={labelError}>
        {(props) => (
          <Input
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('common.fields.type')}>
        {(props) => (
          <Select
            value={draft.kind}
            onChange={(kind) => setDraft({ ...draft, kind })}
            options={toOptions(TEMPLATE_KINDS, (kind) =>
              t(templateKindLabelKey[kind]),
            )}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('settings.templates.body')}
        required={!bodyOptional}
        error={bodyError}
        /* The hint changes with the kind because the field means two different
           things: verbatim copy on a document, or a private remark on a charge
           line nobody outside this company reads. */
        hint={
          bodyOptional
            ? t('settings.templates.bodyHintCostItem')
            : t('settings.templates.bodyHint')
        }
      >
        {(props) => (
          <Textarea
            rows={bodyOptional ? 2 : 5}
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* Removal lives in the BODY rather than beside the submit, because
          FormSheet has no slot for a secondary destructive action and widening
          it is a change to a primitive twenty sheets share.
          NO CONFIRMATION STEP, unlike CargoFormSheet's two-press delete, and
          the difference is deliberate: 0033 copies a template's words onto every
          quote that used it, so deleting one cannot alter a document anybody has
          sent. It removes an entry from a picker. A confirmation dialog for an
          act with no consequence is how people learn to dismiss them. */}
      {onRemove ? (
        <div className="border-line-hair flex flex-col gap-1.5 border-t pt-3">
          <span className="text-text-muted text-[12px] leading-snug font-light">
            {t('settings.templates.removeHint')}
          </span>
          <div>
            <Button
              variant="danger"
              size="sm"
              onClick={onRemove}
              disabled={pending || removePending}
            >
              {t('common.actions.delete')}
            </Button>
          </div>
        </div>
      ) : null}
    </FormSheet>
  )
}

const emptyTemplate: TemplateDraft = {
  label: '',
  body: '',
  kind: 'not-included',
}
